import { Component, ErrorInfo, ReactNode, useCallback } from 'react';
import { ActionType, ComponentType } from '@cbhq/client-analytics';

import { useAnalytics } from '../hooks/useAnalytics';

type InnerErrorBoundaryProps = {
  children: ReactNode;
  onReloadPress?: () => void;
  onBoundaryError?: (error: Error, errorInfo: ErrorInfo) => void;
  fallback?: ReactNode;
};
type InnerErrorBoundaryState = { hasError: boolean };

export class InnerErrorBoundary extends Component<
  InnerErrorBoundaryProps,
  InnerErrorBoundaryState
> {
  constructor(props: InnerErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onBoundaryError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (!this.props.fallback) {
        return null;
      }

      return this.props.fallback;
    }

    return this.props.children;
  }
}

type ErrorBoundaryProps = { children: ReactNode; fallback?: ReactNode };

export function ErrorBoundary(props: ErrorBoundaryProps) {
  const { logEvent } = useAnalytics();
  const handleError = useCallback(
    (error: Error, errorInfo: ErrorInfo) => {
      logEvent('id_nav_error_boundary_error', {
        action: ActionType.render,
        componentType: ComponentType.page,
        error: error.message ?? 'unknown error',
        errorInfo: errorInfo.componentStack ?? 'unknown error info',
      });
    },
    [logEvent],
  );

  return <InnerErrorBoundary onBoundaryError={handleError} {...props} />;
}
